/**
 * @zendir/ui - Skeleton Components
 *
 * Loading skeleton components with Apple-style shimmer animation.
 * Used for loading states before data arrives.
 *
 * Features:
 * - Smooth shimmer animation
 * - Multiple variants (text, card, chart, circle)
 * - Respects reduced-motion preferences
 * - Matches component sizes for seamless transition
 */

import React, { memo } from "react";
import { classNames } from "../utils";

// ============================================================================
// Shared Styles
// ============================================================================

const SHIMMER_KEYFRAMES = `
@keyframes skeletonShimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
`;

const getShimmerStyle = (
  width?: string | number,
  height?: string | number,
): React.CSSProperties => ({
  width: width ?? "100%",
  height: height ?? 16,
  backgroundColor:
    "var(--color-background-interactive-default, rgba(255,255,255,0.1))",
  backgroundImage:
    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)",
  backgroundSize: "200% 100%",
  backgroundRepeat: "no-repeat",
  animation: "skeletonShimmer 1.5s ease-in-out infinite",
  borderRadius: 4,
});

// ============================================================================
// SkeletonText
// ============================================================================

export interface SkeletonTextProps {
  /** Number of lines to render */
  lines?: number;
  /** Width of the last line (as percentage or px) */
  lastLineWidth?: string | number;
  /** Height of each line */
  lineHeight?: number;
  /** Gap between lines */
  gap?: number;
  /** Custom className */
  className?: string;
}

/**
 * SkeletonText - Placeholder for text content
 *
 * @example
 * ```tsx
 * <SkeletonText lines={3} lastLineWidth="60%" />
 * ```
 */
export const SkeletonText = memo(function SkeletonText({
  lines = 1,
  lastLineWidth = "80%",
  lineHeight = 14,
  gap = 8,
  className = "",
}: SkeletonTextProps): React.ReactElement {
  return (
    <div
      className={classNames("zendir-skeleton-text", className)}
      role="presentation"
      aria-hidden="true"
      style={{
        display: "flex",
        flexDirection: "column",
        gap,
      }}
    >
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          style={{
            ...getShimmerStyle(
              i === lines - 1 ? lastLineWidth : "100%",
              lineHeight,
            ),
          }}
        />
      ))}
      <style>{SHIMMER_KEYFRAMES}</style>
    </div>
  );
});

// ============================================================================
// SkeletonBox
// ============================================================================

export interface SkeletonBoxProps {
  /** Width of the box */
  width?: string | number;
  /** Height of the box */
  height?: string | number;
  /** Border radius */
  borderRadius?: number;
  /** Custom className */
  className?: string;
}

/**
 * SkeletonBox - Generic rectangular placeholder
 *
 * @example
 * ```tsx
 * <SkeletonBox width={200} height={100} />
 * ```
 */
export const SkeletonBox = memo(function SkeletonBox({
  width = "100%",
  height = 100,
  borderRadius = 4,
  className = "",
}: SkeletonBoxProps): React.ReactElement {
  return (
    <div
      className={classNames("zendir-skeleton-box", className)}
      role="presentation"
      aria-hidden="true"
      style={{
        ...getShimmerStyle(width, height),
        borderRadius,
      }}
    >
      <style>{SHIMMER_KEYFRAMES}</style>
    </div>
  );
});

// ============================================================================
// SkeletonCircle
// ============================================================================

export interface SkeletonCircleProps {
  /** Diameter of the circle */
  size?: number;
  /** Custom className */
  className?: string;
}

/**
 * SkeletonCircle - Circular placeholder (avatars, icons)
 *
 * @example
 * ```tsx
 * <SkeletonCircle size={48} />
 * ```
 */
export const SkeletonCircle = memo(function SkeletonCircle({
  size = 40,
  className = "",
}: SkeletonCircleProps): React.ReactElement {
  return (
    <div
      className={classNames("zendir-skeleton-circle", className)}
      role="presentation"
      aria-hidden="true"
      style={{
        ...getShimmerStyle(size, size),
        borderRadius: "50%",
        flexShrink: 0,
      }}
    >
      <style>{SHIMMER_KEYFRAMES}</style>
    </div>
  );
});

// ============================================================================
// SkeletonCard
// ============================================================================

export interface SkeletonCardProps {
  /** Card height */
  height?: number;
  /** Show header placeholder */
  showHeader?: boolean;
  /** Show content lines */
  showContent?: boolean;
  /** Number of content lines */
  contentLines?: number;
  /** Custom className */
  className?: string;
}

/**
 * SkeletonCard - Card-shaped placeholder matching component cards
 *
 * @example
 * ```tsx
 * <SkeletonCard showHeader showContent contentLines={4} />
 * ```
 */
export const SkeletonCard = memo(function SkeletonCard({
  height,
  showHeader = true,
  showContent = true,
  contentLines = 3,
  className = "",
}: SkeletonCardProps): React.ReactElement {
  return (
    <div
      className={classNames("zendir-skeleton-card", className)}
      role="presentation"
      aria-label="Loading..."
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: 16,
        minHeight: height,
        backgroundColor: "var(--zendir-bg-surface, #1b2d3e)",
        borderRadius: 8,
        border: "1px solid var(--zendir-border-default, rgba(255,255,255,0.1))",
      }}
    >
      {/* Header */}
      {showHeader && (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <SkeletonCircle size={32} />
          <div style={{ flex: 1 }}>
            <SkeletonText lines={1} lineHeight={16} />
            <div style={{ height: 6 }} />
            <SkeletonText lines={1} lineHeight={12} lastLineWidth="50%" />
          </div>
        </div>
      )}

      {/* Content */}
      {showContent && (
        <div style={{ flex: 1 }}>
          <SkeletonText lines={contentLines} gap={10} lineHeight={12} />
        </div>
      )}

      <style>{SHIMMER_KEYFRAMES}</style>
    </div>
  );
});

// ============================================================================
// SkeletonChart
// ============================================================================

export interface SkeletonChartProps {
  /** Chart height */
  height?: number;
  /** Chart type visualization */
  type?: "line" | "bar" | "area";
  /** Custom className */
  className?: string;
}

/**
 * SkeletonChart - Chart placeholder with visualization hint
 *
 * @example
 * ```tsx
 * <SkeletonChart height={200} type="line" />
 * ```
 */
export const SkeletonChart = memo(function SkeletonChart({
  height = 200,
  type = "line",
  className = "",
}: SkeletonChartProps): React.ReactElement {
  return (
    <div
      className={classNames("zendir-skeleton-chart", className)}
      role="presentation"
      aria-label="Loading chart..."
      style={{
        position: "relative",
        height,
        backgroundColor: "var(--zendir-bg-surface, #1b2d3e)",
        borderRadius: 8,
        border: "1px solid var(--zendir-border-default, rgba(255,255,255,0.1))",
        overflow: "hidden",
      }}
    >
      {/* Chart area */}
      <div
        style={{
          position: "absolute",
          inset: 16,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: type === "bar" ? 8 : 0,
        }}
      >
        {type === "bar" ? (
          // Bar chart skeleton
          Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              style={{
                ...getShimmerStyle(undefined, `${30 + Math.random() * 50}%`),
                flex: 1,
                borderRadius: "4px 4px 0 0",
              }}
            />
          ))
        ) : (
          // Line/Area chart skeleton
          <svg
            viewBox="0 0 100 50"
            preserveAspectRatio="none"
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            <defs>
              <linearGradient
                id="skeletonGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" stopColor="rgba(255,255,255,0.05)">
                  <animate
                    attributeName="offset"
                    values="-0.5;1.5"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="50%" stopColor="rgba(255,255,255,0.1)">
                  <animate
                    attributeName="offset"
                    values="0;2"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="100%" stopColor="rgba(255,255,255,0.05)">
                  <animate
                    attributeName="offset"
                    values="0.5;2.5"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </stop>
              </linearGradient>
            </defs>
            <path
              d="M0,40 Q15,35 25,30 T50,25 T75,20 T100,15 L100,50 L0,50 Z"
              fill={type === "area" ? "url(#skeletonGradient)" : "none"}
              stroke="url(#skeletonGradient)"
              strokeWidth="2"
            />
          </svg>
        )}
      </div>

      {/* Axis labels */}
      <div
        style={{
          position: "absolute",
          left: 4,
          top: 16,
          bottom: 16,
          width: 24,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonBox key={i} width={24} height={8} />
        ))}
      </div>

      <style>{SHIMMER_KEYFRAMES}</style>
    </div>
  );
});

// ============================================================================
// Skeleton3D
// ============================================================================

export interface Skeleton3DProps {
  /** Container height */
  height?: number;
  /** Custom className */
  className?: string;
}

/**
 * Skeleton3D - 3D viewer placeholder with sphere hint
 *
 * @example
 * ```tsx
 * <Skeleton3D height={400} />
 * ```
 */
export const Skeleton3D = memo(function Skeleton3D({
  height = 400,
  className = "",
}: Skeleton3DProps): React.ReactElement {
  return (
    <div
      className={classNames("zendir-skeleton-3d", className)}
      role="presentation"
      aria-label="Loading 3D view..."
      style={{
        position: "relative",
        height,
        backgroundColor: "var(--zendir-bg-base, #101923)",
        borderRadius: 8,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Spinning sphere hint */}
      <div
        style={{
          width: Math.min(height * 0.5, 200),
          height: Math.min(height * 0.5, 200),
          borderRadius: "50%",
          border: "2px solid rgba(255,255,255,0.1)",
          backgroundImage:
            "linear-gradient(135deg, transparent 40%, rgba(77,172,255,0.1) 50%, transparent 60%)",
          backgroundSize: "200% 200%",
          animation:
            "skeleton3DSpin 3s linear infinite, skeletonShimmer 1.5s ease-in-out infinite",
        }}
      />

      {/* Loading text */}
      <div
        style={{
          position: "absolute",
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: "center",
          fontSize: 12,
          color: "rgba(255,255,255,0.4)",
        }}
      >
        Loading 3D Scene...
      </div>

      <style>{`
        ${SHIMMER_KEYFRAMES}
        @keyframes skeleton3DSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
});
